import { useEffect, useRef, useState } from "react";

interface AutocompleteProps {
  value: string;
  onChange: (v: string) => void;
  onSelect?: (v: string) => void;
  suggestions: string[];
  placeholder?: string;
  className?: string;
  type?: string;
  id?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  filter?: (q: string, items: string[]) => string[];
  maxItems?: number;
}

const defaultFilter = (q: string, items: string[]) => {
  if (!q) return items;
  const lower = q.toLowerCase();
  const starts = items.filter((i) => i.toLowerCase().startsWith(lower));
  const contains = items.filter((i) => !i.toLowerCase().startsWith(lower) && i.toLowerCase().includes(lower));
  return [...starts, ...contains];
};

export function Autocomplete({
  value,
  onChange,
  onSelect,
  suggestions,
  placeholder,
  className,
  type = "text",
  id,
  inputMode,
  filter = defaultFilter,
  maxItems = 8,
}: AutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const filtered = filter(value, suggestions).slice(0, maxItems);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function pick(v: string) {
    onChange(v);
    onSelect?.(v);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter" && filtered[highlight]) {
            e.preventDefault();
            pick(filtered[highlight]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
          {filtered.map((s, i) => (
            <li
              key={s}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-4 py-2 cursor-pointer text-sm ${i === highlight ? "bg-primary/15 text-foreground" : "text-foreground/90"}`}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface AddressFeature {
  display_name: string;
  place_id: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

export function AddressAutocomplete({ value, onChange, placeholder, className, id }: AddressAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [results, setResults] = useState<AddressFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function search(q: string) {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        // Nominatim, restricted to NY state via viewbox + bounded=1
        // Viewbox covers NYC, Long Island, and surrounding NY area
        const url = new URL("https://nominatim.openstreetmap.org/search");
        url.searchParams.set("format", "json");
        url.searchParams.set("addressdetails", "1");
        url.searchParams.set("countrycodes", "us");
        url.searchParams.set("limit", "6");
        url.searchParams.set("viewbox", "-74.30,41.20,-71.85,40.49");
        url.searchParams.set("bounded", "1");
        url.searchParams.set("q", q);
        const res = await fetch(url.toString(), {
          signal: ctrl.signal,
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } catch (err) {
        if ((err as Error).name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }

  function pick(f: AddressFeature) {
    onChange(f.display_name);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
          search(e.target.value);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter" && results[highlight]) {
            e.preventDefault();
            pick(results[highlight]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && (loading || results.length > 0) && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
          {loading && results.length === 0 && (
            <li className="px-4 py-2 text-sm text-muted-foreground">Searching addresses…</li>
          )}
          {results.map((r, i) => (
            <li
              key={r.place_id}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-4 py-2 cursor-pointer text-sm ${i === highlight ? "bg-primary/15 text-foreground" : "text-foreground/90"}`}
            >
              {r.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
